import { useEffect, useReducer } from 'react'

// Reactive Vars are a concept popularized in React by Apollo Client. A reactive
// var is a var created at the top level of a file using the `makeVar` function
// below. It holds a tiny amount of global state that can be updated and
// accessed anywhere. Unlike most alternative solutions, it only triggers a
// rerender of subscribed components and not a single top-level component. This
// avoids the majority of the performance issues in React caused by global
// state. It's also a great way to isolate the global state to within components
// that define it and components that use it. In other words, we don't need to
// maintain a separate global file with all global states mixed together.

// We would use the

// The code below comes originally from https://github.com/jorbuedo/react-reactive-var.
// Since the library only has 2 stars and the below code is all it takes, I felt
// it better to maintain a copy.

// https://www.apollographql.com/docs/react/local-state/reactive-variables/

export type ReactiveVar<T> = {
  (newValue?: T | ((value: T) => T)): T
  subscribe: (handler: (value: T) => void) => () => void
  unsubscribe: (handler: (value: T) => void) => void
}

type EqualsFunc<T> = (a: T, b: T) => boolean

export const makeVar = <T>(
  initialValue: T,
  equalsFunc?: EqualsFunc<T>,
): ReactiveVar<T> => {
  let value = initialValue
  type Handler = (value: T) => void
  const subscribers = new Set<Handler>()

  const reactiveVar = (newValue?: T | ((value: T) => T)) => {
    if (newValue !== undefined) {
      let nextValue = value

      if (newValue instanceof Function) {
        nextValue = newValue(value)
      } else {
        nextValue = newValue
      }

      const valueChanged = equalsFunc
        ? !equalsFunc(nextValue, value)
        : nextValue !== value
      value = nextValue

      if (valueChanged) {
        subscribers.forEach((handler) => handler(value))
      }
    }
    return value
  }

  reactiveVar.subscribe = (handler: Handler) => {
    subscribers.add(handler)
    return () => subscribers.delete(handler)
  }

  reactiveVar.unsubscribe = (handler: Handler) => {
    subscribers.delete(handler)
  }

  return reactiveVar
}

export const useReactiveVar = <T>(reactiveVar: ReactiveVar<T>) => {
  const handler = useReducer((x) => x + 1, 0)[1] as () => void

  useEffect(() => {
    reactiveVar.subscribe(handler)
    return () => reactiveVar.unsubscribe(handler)
  }, [reactiveVar, handler])

  return reactiveVar()
}
